import { environment } from '@/assets/environment';
import { RequestEssenceType } from '@/models/entities/request-essence.model';
import { IErrorResponse } from '@/models/errors/error-response.model';
import { IHelperEntityTire } from '@/models/store/tires/helper-entity-tire.model';
import { ITire } from '@/models/store/tires/tire.model';
import { ITiresState } from '@/models/store/tires/tires-state.model';
import { ITireFilter } from '../../models/store/filter-data/tire-filter.model';
import { Module } from 'vuex';
import { IFilterData } from '@/models/store/filter-data/filter-data.model';
import { ICargoTire } from '@/models/store/tires/cargo-tire.model';
import { CargoFilterData } from '@/models/store/tires/cargo-filter-data.model';
import { store } from '..';

export const tireModule: Module<ITiresState, unknown> = {
    state: {
        tires: [],
        helperTireEssences: [],
        filterCargoData: {
            brands: [],
            models: [],
            sizes: [],
            applicabilities: [],
        },
        cargoTires: [],
    },

    actions: {
        fetchHelperTire: async ({ commit }, payload: RequestEssenceType) => {
            const params = new URLSearchParams();
            if (payload) params.append('type', payload);

            const response = await fetch(`${environment.baseUrl}/api/helper-tire?` + params, {
                method: 'get',
                headers: {
                    'x-app': 'secret_admin',
                    'Content-type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });
                return;
            }

            const essences = (await response.json()).data;
            commit('setHelperEssencesTire', essences);
        },

        fetchTires: async (
            { commit },
            payload: { pageNumber: number; tireFilter: ITireFilter; count: number },
        ): Promise<void> => {
            // this.filter.tireFilter.brand = filterService.transformQueryParams(this.filter.tireFilter.brand as string[]);
            // this.filter.tireFilter.season = filterService.transformQueryParams(this.filter.tireFilter.season as string[]);
            const params = new URLSearchParams();
            params.append('count', String(payload.count));
            params.append('pageNumber', `${payload.pageNumber}`);
            if (payload.tireFilter?.width) params.append('width', payload.tireFilter.width);
            if (payload.tireFilter?.height) params.append('height', payload.tireFilter.height);
            if (payload.tireFilter?.diameter)
                params.append('diameter', payload.tireFilter.diameter);
            if (payload.tireFilter?.brand?.length) {
                params.append('brand', payload.tireFilter.brand as string);
            }
            if (payload.tireFilter?.season?.length) {
                params.append('season', payload.tireFilter.season as string);
            }
            if (payload.tireFilter.stud) params.append('stud', payload.tireFilter.stud);
            if (payload.tireFilter.possibilityStudding)
                params.append('possibilityStudding', payload.tireFilter.possibilityStudding);

            const response = await fetch(`${environment.baseUrl}/api/tire?` + params, {
                method: 'get',
                headers: {
                    'x-app': 'secret_admin',
                    'Content-type': 'application/json',
                    auth: 'db75 XVU3d82G',
                },
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });

                return;
            }

            const tiresData = await response.json();
            commit('setPaginationLength', tiresData.length);
            commit('setTires', tiresData.data);
        },

        fetchCargoTires: async (
            { commit, state },
            {
                pageNumber,
                count,
                filterData,
            }: { pageNumber: number; count: number; filterData: IFilterData },
        ): Promise<ICargoTire | undefined> => {
            const params = new URLSearchParams();
            params.append('page', `${store.getters.getCurrentPageNumber}`);
            params.append('count', `${count}`);

            if (store.getters.getFilterData?.cargoFilters) {
                const { brand, model, applicabilities, size } =
                    store.getters.getFilterData?.cargoFilters;

                if (brand) params.append('brand', brand);
                if (model) params.append('model', model);
                if (applicabilities) params.append('applicabilities', applicabilities);
                if (size) params.append('size', size);
            }

            const response = await fetch(`${environment.baseUrl}/api/cargo/tire?` + params, {
                method: 'get',
                headers: {
                    'x-app': 'secret_admin',
                    'Content-type': 'application/json',
                    auth: 'db75 XVU3d82G',
                },
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });

                return;
            }

            const { length, data } = await response.json();
            commit('setPaginationLength', length);
            commit('setCargoTires', data);

            return data;
        },

        fetchCargoFiltersData: async ({ commit }): Promise<CargoFilterData | undefined> => {
            const response = await fetch(`${environment.baseUrl}/api/cargo/tire/filter/data`, {
                method: 'get',
                headers: {
                    'x-app': 'secret_admin',
                    'Content-type': 'application/json',
                    auth: 'db75 XVU3d82G',
                },
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });

                return;
            }

            const result = await response.json();
            commit('setCargoFilterData', result);

            return result;
        },
    },

    mutations: {
        setHelperEssencesTire: (store, payload: IHelperEntityTire[]) => {
            store.helperTireEssences = [...payload];
        },

        setTires: (store, payload: ITire[]) => {
            store.tires = [...payload];
        },
        setCargoFilterData: (store, payload: CargoFilterData) => {
            store.filterCargoData = payload;
        },

        setCargoTires: (store, payload: ICargoTire[]) => {
            store.cargoTires = [...payload];
        },
    },

    getters: {
        getHelperEntities: (store) => store.helperTireEssences,
        getTires: (store) => store.tires,
        getFilterCargoData: (store) => store.filterCargoData,
        getCargoTires: (store) => store.cargoTires,
    },
};
